import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Image,
  Search,
  User,
  Tag,
  AlertCircle,
  Check,
  Loader2,
  AlertTriangle,
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Info,
} from 'lucide-react';
import {
  BulkUploadDocumentView,
  BulkUploadDocumentStatus,
  EmployeeSimpleView,
  CompanyAttachmentTypeView,
  ExtractedDocumentFields,
  getEmployeesForMapping,
  getAttachmentTypes,
} from '../../../../api/General/bulkUpload';

interface OcrReviewStepProps {
  documents: BulkUploadDocumentView[];
  ocrRunning: boolean;
  ocrError: string | null;
  mappings: Record<number, { userId: string; companyAttachmentTypeId: number; expiryDate?: string; reminderDays?: number }>;
  onMappingsChange: (
    mappings: Record<number, { userId: string; companyAttachmentTypeId: number; expiryDate?: string; reminderDays?: number }>
  ) => void;
  onSwitchToManual: () => void;
}

function getConfidenceColor(confidence: number | undefined): {
  bg: string;
  text: string;
  label: string;
} {
  if (confidence == null || confidence === 0) {
    return {
      bg: 'bg-gray-100 dark:bg-gray-800',
      text: 'text-gray-500 dark:text-gray-400',
      label: 'N/A',
    };
  }
  if (confidence >= 80) {
    return {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-700 dark:text-green-400',
      label: 'High',
    };
  }
  if (confidence >= 50) {
    return {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      text: 'text-amber-700 dark:text-amber-400',
      label: 'Medium',
    };
  }
  return {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    label: 'Low',
  };
}

function parseExtractedFields(json?: string): ExtractedDocumentFields | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as ExtractedDocumentFields;
  } catch {
    return null;
  }
}

export default function OcrReviewStep({
  documents,
  ocrRunning,
  ocrError,
  mappings,
  onMappingsChange,
  onSwitchToManual,
}: OcrReviewStepProps) {
  const { t } = useTranslation('common');
  const [employees, setEmployees] = useState<EmployeeSimpleView[]>([]);
  const [attachmentTypes, setAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDocId, setExpandedDocId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingRefs(true);
      try {
        const [empResult, typeResult] = await Promise.all([
          getEmployeesForMapping(),
          getAttachmentTypes(),
        ]);
        if (empResult.errors?.length === 0) setEmployees(empResult.payload);
        if (typeResult.errors?.length === 0) setAttachmentTypes(typeResult.payload);
      } finally {
        setLoadingRefs(false);
      }
    };
    fetchData();
  }, []);

  // Pre-populate mappings from OCR auto-matched results
  useEffect(() => {
    if (ocrRunning || loadingRefs) return;

    const newMappings = { ...mappings };
    let changed = false;

    for (const doc of documents) {
      if (
        !newMappings[doc.id] &&
        (doc.userId || doc.companyAttachmentTypeId || doc.expiryDate)
      ) {
        newMappings[doc.id] = {
          userId: doc.userId ?? '',
          companyAttachmentTypeId: doc.companyAttachmentTypeId ?? 0,
          expiryDate: doc.expiryDate ?? undefined,
          reminderDays: doc.reminderDays ?? undefined,
        };
        changed = true;
      }
    }

    if (changed) {
      onMappingsChange(newMappings);
    }
  }, [documents, ocrRunning, loadingRefs]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredDocuments = useMemo(() => {
    if (!searchTerm) return documents;
    const term = searchTerm.toLowerCase();
    return documents.filter((d) => d.originalFileName.toLowerCase().includes(term));
  }, [documents, searchTerm]);

  const ocrCompletedCount = documents.filter(
    (d) =>
      d.status === BulkUploadDocumentStatus.OcrCompleted ||
      d.status === BulkUploadDocumentStatus.Mapped
  ).length;

  const mappedCount = documents.filter(
    (d) => mappings[d.id]?.userId && mappings[d.id]?.companyAttachmentTypeId
  ).length;

  const handleMappingChange = (
    docId: number,
    field: 'userId' | 'companyAttachmentTypeId',
    value: string | number
  ) => {
    const current = mappings[docId] || { userId: '', companyAttachmentTypeId: 0 };
    onMappingsChange({
      ...mappings,
      [docId]: { ...current, [field]: value },
    });
  };

  const getFileIcon = (contentType: string) => {
    if (contentType === 'application/pdf') {
      return <FileText className="w-4 h-4 text-red-500" />;
    }
    return <Image className="w-4 h-4 text-blue-500" />;
  };

  const getDocStatusIcon = (doc: BulkUploadDocumentView) => {
    if (doc.status === BulkUploadDocumentStatus.OcrProcessing) {
      return <Loader2 className="w-4 h-4 text-primary animate-spin" />;
    }
    if (doc.status === BulkUploadDocumentStatus.Uploaded) {
      return <div className="w-4 h-4 rounded-full border-2 border-border" />;
    }
    if (
      doc.status === BulkUploadDocumentStatus.Mapped &&
      doc.ocrConfidence != null
    ) {
      return <Check className="w-4 h-4 text-green-500" />;
    }
    if (doc.status === BulkUploadDocumentStatus.OcrCompleted) {
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
    return null;
  };

  // Show full-page loading only before the first document completes.
  // Once any doc finishes, show the table with per-file spinners for remaining docs.
  if (ocrRunning && ocrCompletedCount === 0) {
    return (
      <div className="px-6 py-16">
        <div className="flex flex-col items-center gap-4 text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {t('Analyzing Documents with AI Vision')}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('Detecting employee names and document types from file content...')}
            </p>
          </div>
          <div className="w-64 bg-muted rounded-full h-2 mt-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{
                width: `${documents.length > 0 ? (ocrCompletedCount / documents.length) * 100 : 0}%`,
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {ocrCompletedCount}/{documents.length} {t('files')}
          </p>
        </div>
      </div>
    );
  }

  // Show OCR error with fallback option
  if (ocrError) {
    return (
      <div className="px-6 py-12">
        <div className="flex flex-col items-center gap-4 text-center max-w-md mx-auto">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {t('OCR service is currently unavailable')}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{ocrError}</p>
          </div>
          <button
            type="button"
            onClick={onSwitchToManual}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            data-testid="switch-to-manual-btn"
          >
            <ArrowRightLeft className="w-4 h-4" />
            {t('Switch to Manual Entry')}
          </button>
        </div>
      </div>
    );
  }

  if (loadingRefs) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('OCR Review')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('Review detected results and correct any mismatches')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
              mappedCount === documents.length
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`}
          >
            {mappedCount === documents.length ? (
              <Check className="w-3 h-3" />
            ) : (
              <AlertCircle className="w-3 h-3" />
            )}
            {mappedCount}/{documents.length} {t('mapped')}
          </span>
          <button
            type="button"
            onClick={onSwitchToManual}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
            data-testid="switch-to-manual-link"
          >
            <ArrowRightLeft className="w-3 h-3" />
            {t('Switch to Manual Entry')}
          </button>
        </div>
      </div>

      {/* OCR progress bar (during partial processing) */}
      {ocrRunning && (
        <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
            <span className="text-sm font-medium text-foreground">
              {t('OCR processing in progress...')}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-500"
              style={{
                width: `${documents.length > 0 ? (ocrCompletedCount / documents.length) * 100 : 0}%`,
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {ocrCompletedCount}/{documents.length} {t('files')}
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('Search files...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          data-testid="ocr-review-search"
        />
      </div>

      {/* Document table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[30%]">
                  {t('File')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[10%]">
                  {t('Confidence')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[30%]">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {t('Employee')}
                  </div>
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[30%]">
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    {t('Document Type')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredDocuments.map((doc) => {
                const mapping = mappings[doc.id];
                const isMapped =
                  mapping?.userId && mapping?.companyAttachmentTypeId;
                const conf = getConfidenceColor(doc.ocrConfidence);
                const isProcessing =
                  doc.status === BulkUploadDocumentStatus.OcrProcessing ||
                  doc.status === BulkUploadDocumentStatus.Uploaded;

                return (
                  <React.Fragment key={doc.id}>
                  <tr
                    className={`transition-colors ${
                      isMapped
                        ? 'bg-green-50/50 dark:bg-green-900/5'
                        : 'hover:bg-muted/30'
                    }`}
                  >
                    {/* File info */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {getDocStatusIcon(doc)}
                        {getFileIcon(doc.contentType)}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate max-w-[200px]">
                            {doc.originalFileName}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {(doc.fileSize / 1024).toFixed(1)} KB
                            </span>
                            {(doc.matchReasoning || doc.extractedFieldsJson) && (
                              <button
                                type="button"
                                onClick={() => setExpandedDocId(expandedDocId === doc.id ? null : doc.id)}
                                className="text-xs text-primary hover:text-primary/80 flex items-center gap-0.5"
                              >
                                {expandedDocId === doc.id ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                                {t('Details')}
                              </button>
                            )}
                            {doc.ocrDetectedName && (
                              <span className="text-xs text-muted-foreground italic truncate max-w-[120px]">
                                &rarr; {doc.ocrDetectedName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Confidence */}
                    <td className="px-4 py-3">
                      {isProcessing ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-primary font-medium">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {t('Analyzing...')}
                        </span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${conf.bg} ${conf.text}`}
                        >
                          {doc.ocrConfidence != null
                            ? `${Math.round(doc.ocrConfidence)}%`
                            : '—'}
                        </span>
                      )}
                      {doc.matchReasoning?.includes('[Vision') && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 ml-1">
                          {t('Vision')}
                        </span>
                      )}
                    </td>

                    {/* Employee selector */}
                    <td className="px-4 py-3">
                      {isProcessing ? (
                        <div className="h-8 bg-muted/50 rounded animate-pulse" />
                      ) : (
                        <div>
                          <select
                            value={mapping?.userId || ''}
                            onChange={(e) =>
                              handleMappingChange(doc.id, 'userId', e.target.value)
                            }
                            className={`w-full px-2.5 py-1.5 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              doc.ocrConfidence != null &&
                              doc.ocrConfidence < 50 &&
                              !mapping?.userId
                                ? 'border-red-300 dark:border-red-700'
                                : 'border-border'
                            }`}
                            data-testid={`ocr-employee-select-${doc.id}`}
                          >
                            <option value="">{t('Select employee...')}</option>
                            {employees.map((emp) => (
                              <option key={emp.id} value={emp.id}>
                                {emp.fullName}
                                {emp.department ? ` (${emp.department})` : ''}
                              </option>
                            ))}
                          </select>
                          {doc.ocrDetectedName &&
                            !mapping?.userId && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                {t('Detected')}: {doc.ocrDetectedName}
                              </p>
                            )}
                        </div>
                      )}
                    </td>

                    {/* Document type selector */}
                    <td className="px-4 py-3">
                      {isProcessing ? (
                        <div className="h-8 bg-muted/50 rounded animate-pulse" />
                      ) : (
                        <div>
                          <select
                            value={mapping?.companyAttachmentTypeId || ''}
                            onChange={(e) =>
                              handleMappingChange(
                                doc.id,
                                'companyAttachmentTypeId',
                                Number(e.target.value)
                              )
                            }
                            className={`w-full px-2.5 py-1.5 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              doc.ocrConfidence != null &&
                              doc.ocrConfidence < 50 &&
                              !mapping?.companyAttachmentTypeId
                                ? 'border-red-300 dark:border-red-700'
                                : 'border-border'
                            }`}
                            data-testid={`ocr-type-select-${doc.id}`}
                          >
                            <option value="">{t('Select type...')}</option>
                            {attachmentTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.attachmentType}
                              </option>
                            ))}
                          </select>
                          {doc.attachmentTypeAutoCreated && (
                            <span className="inline-flex items-center gap-1 mt-0.5 text-xs text-purple-600 dark:text-purple-400">
                              <Sparkles className="w-3 h-3" />
                              {t('Auto-created')}
                            </span>
                          )}
                          {doc.ocrDetectedType &&
                            !mapping?.companyAttachmentTypeId && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                {t('Detected')}: {doc.ocrDetectedType}
                              </p>
                            )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedDocId === doc.id && (
                    <tr key={`${doc.id}-detail`} className="bg-muted/20">
                      <td colSpan={4} className="px-4 py-3">
                        <div className="space-y-3">
                          {/* Match Reasoning */}
                          {doc.matchReasoning && (
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-muted-foreground">{t('Match Reasoning')}</p>
                                <p className="text-sm text-foreground">{doc.matchReasoning}</p>
                              </div>
                            </div>
                          )}

                          {/* Extracted Fields */}
                          {(() => {
                            const fields = parseExtractedFields(doc.extractedFieldsJson);
                            if (!fields) return null;
                            const entries = Object.entries(fields).filter(([, v]) => v != null && v !== '');
                            if (entries.length === 0) return null;

                            return (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1.5">{t('Extracted Fields')}</p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">
                                  {entries.map(([key, value]) => (
                                    <div key={key} className="text-sm">
                                      <span className="text-muted-foreground">{t(key)}: </span>
                                      <span className="text-foreground font-medium">{String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />
          {t('High confidence')} (≥80%)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500" />
          {t('Medium confidence')} (50-79%)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
          {t('Low confidence')} (&lt;50%)
        </span>
      </div>

      {/* Error doc messages */}
      {documents.some((d) => d.errorMessage && d.status === BulkUploadDocumentStatus.OcrCompleted) && (
        <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/10 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('Some documents could not be analyzed by OCR. Please assign them manually.')}
          </p>
        </div>
      )}
    </div>
  );
}
